import { Inject, Injectable, Optional } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse, HttpEvent } from '@angular/common/http';

import { Observable } from 'rxjs';

import { Configuration } from '../configuration';
import { BusinessPartnerResponse, BusinessPartner } from '@bo-client/shared/models/businessPartner';
import { ConfigService } from '@bo-client/config/config.service';
import { DataShareService } from '@bo-client/shared/services/data-share.service';

@Injectable()
export class BusinessPartnersService {
  public defaultHeaders = new HttpHeaders();
  public configuration = new Configuration();

  constructor(
    protected httpClient: HttpClient,
    @Optional() configuration: Configuration,
    private configService: ConfigService,
    private dataShareService: DataShareService
  ) {}

  /**
   * @param consumes string[] mime-types
   * @return true: consumes contains 'multipart/form-data', false: otherwise
   */
  private canConsumeForm(consumes: string[]): boolean {
    const form = 'multipart/form-data';
    for (const consume of consumes) {
      if (form === consume) {
        return true;
      }
    }
    return false;
  }

  // Todo implement the searchquery
  public find(searchQuery: string) {
    return this.get('body', false, searchQuery);
  }
  /**
   * getContracts
   * gets all the contract offers of the specified tenant
   * @param observe set whether or not to return the data Observable as the body, response or events. defaults to returning the body.
   * @param reportProgress flag to report request and response progress.
   */

  public get(observe: any = 'body', reportProgress = false, searchQuery = ''): Observable<any> {
    let headers = this.defaultHeaders;

    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = ['application/json'];
    // TODO: we should refactor this so all api's look the same and the tenant always gets passed as a X-TENANT header.
    // But this will need work on the backend side since Spring Data Rest repositories currently can't handle the header parameter as filter value
    return this.httpClient.get<BusinessPartnerResponse>(
      `${this.configService.getSettings(
        'serverUrl'
      )}/data/businessPartners/search/findAllByTenant_Id?tenantId=` + this.dataShareService.selectedTenant.id,
      {
        withCredentials: this.configuration.withCredentials,
        headers: headers,
        observe: observe,
        reportProgress: reportProgress
      }
    );
  }

  public patch(id: number, body: BusinessPartner): Observable<any> {
    let headers = this.defaultHeaders;

    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = ['application/json'];
    const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      headers = headers.set('Content-Type', httpContentTypeSelected);
    }

    return this.httpClient.patch<BusinessPartner>(
      `${this.configService.getSettings('serverUrl')}/data/businessPartners/${encodeURIComponent(
        String(id)
      )}`,
      body,
      {
        params: {},
        withCredentials: this.configuration.withCredentials,
        headers: headers
      }
    );
  }

  public create(body: BusinessPartner): Observable<any> {
    let headers = this.defaultHeaders;

    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = ['application/json'];
    const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      headers = headers.set('Content-Type', httpContentTypeSelected);
    }

    return this.httpClient.post<BusinessPartner>(
      `${this.configService.getSettings('serverUrl')}/data/businessPartners/`,
      body,
      {
        params: {},
        withCredentials: this.configuration.withCredentials,
        headers: headers
      }
    );
  }

  public deleteBusinessPrtner(id: number): Observable<any> {
    let headers = this.defaultHeaders;

    // to determine the Accept header
    const httpHeaderAccepts: string[] = ['application/json'];
    const httpHeaderAcceptSelected: string | undefined =
      this.configuration.selectHeaderAccept(httpHeaderAccepts);
    if (httpHeaderAcceptSelected !== undefined) {
      headers = headers.set('Accept', httpHeaderAcceptSelected);
    }

    // to determine the Content-Type header
    const consumes: string[] = ['application/json'];
    const httpContentTypeSelected: string | undefined = this.configuration.selectHeaderContentType(consumes);
    if (httpContentTypeSelected !== undefined) {
      headers = headers.set('Content-Type', httpContentTypeSelected);
    }

    return this.httpClient.delete<BusinessPartner>(
      `${this.configService.getSettings('serverUrl')}/data/businessPartners/${encodeURIComponent(
        String(id)
      )}`,
      {
        params: {},
        withCredentials: this.configuration.withCredentials,
        headers: headers
      }
    );
  }
}
